import { useState, useEffect } from 'react';
import { Joyride, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const STORAGE_KEY = '_mc_tour_completed';

/**
 * First-run interactive tour for new admins (post signup-tenant).
 *
 * Activates when:
 *  - user is admin
 *  - !localStorage[STORAGE_KEY]
 *  - tenant has very little data (0 clients AND 0 requests)
 *
 * Relies on `data-tour="<id>"` attributes added to Sidebar nav buttons and user footer.
 */
export default function AppTour() {
  const { user } = useAuth();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [ready, setReady] = useState(false);

  // Decide whether to auto-start the tour
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    if (localStorage.getItem(STORAGE_KEY) === '1') return;

    let cancelled = false;
    // Wait a tick so the sidebar has mounted and data-tour targets exist
    const kick = async () => {
      try {
        const d = await api.adminDashboard();
        const onb = d?.onboarding || {};
        const lowData = (onb.clients_count || 0) === 0 && (onb.requests_count || 0) === 0;
        if (!cancelled && lowData) {
          // Slight delay to let layout settle
          setTimeout(() => { if (!cancelled) { setReady(true); setRun(true); } }, 600);
        }
      } catch {
        // If dashboard fails, still show the tour (new tenant, empty DB is likely)
        if (!cancelled) setTimeout(() => { if (!cancelled) { setReady(true); setRun(true); } }, 600);
      }
    };
    kick();
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  const steps = [
    {
      target: 'body',
      placement: 'center',
      title: 'Bienvenido a MedCover Pro',
      content: 'Tu empresa está lista. Sigue estos pasos rápidos para configurar tu plataforma y empezar a gestionar eventos.',
      disableBeacon: true,
    },
    {
      target: '[data-tour="clients"]',
      title: 'Paso 1 — Clientes',
      content: 'Primero da de alta un cliente: empresas organizadoras de eventos (federaciones, clubes, organizadores).',
      disableBeacon: true,
    },
    {
      target: '[data-tour="professionals"]',
      title: 'Paso 2 — Profesionales',
      content: 'Después, registra tus médicos, enfermeros y TES. Pueden auto-registrarse y tú los apruebas.',
      disableBeacon: true,
    },
    {
      target: '[data-tour="resources"]',
      title: 'Paso 3 — Recursos',
      content: 'Configura tus vehículos (SVA, SVAE, SVB, TSNU) y sus bases operativas.',
      disableBeacon: true,
    },
    {
      target: '[data-tour="requests"]',
      title: 'Paso 4 — Solicitudes',
      content: 'Aquí creas la primera solicitud de cobertura para un evento. Generas presupuesto y asignas recursos.',
      disableBeacon: true,
    },
    {
      target: '[data-tour="users"]',
      title: 'Usuarios',
      content: 'Desde Usuarios invitas a gente con magic links — sin passwords por email.',
      disableBeacon: true,
    },
    {
      target: '[data-tour="user-footer"]',
      title: 'Mi cuenta',
      content: 'En tu perfil puedes activar 2FA, conectar Google Calendar y gestionar tus ajustes personales.',
      disableBeacon: true,
      placement: 'right',
    },
  ];

  // react-joyride v3 uses `onEvent(data, controls)`. Previous v2 API was `callback(data)`.
  // Without this rename the handler never fires, and in controlled mode (stepIndex prop)
  // the Next button appears to "do nothing" → overlay remains, app unreachable.
  const handleEvent = (data) => {
    const { status, type, action, index, size } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      localStorage.setItem(STORAGE_KEY, '1');
      setRun(false);
      setStepIndex(0);
      return;
    }

    if (action === ACTIONS.CLOSE || action === ACTIONS.SKIP) {
      localStorage.setItem(STORAGE_KEY, '1');
      setRun(false);
      setStepIndex(0);
      return;
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const delta = action === ACTIONS.PREV ? -1 : 1;
      const next = index + delta;
      if (next >= size) {
        localStorage.setItem(STORAGE_KEY, '1');
        setRun(false);
        setStepIndex(0);
      } else {
        setStepIndex(Math.max(0, next));
      }
    }
  };

  if (!ready || !user || user.role !== 'admin') return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      options={{
        primaryColor: 'var(--accent)',
        textColor: 'var(--text)',
        backgroundColor: 'var(--card)',
        arrowColor: 'var(--card)',
        overlayColor: 'rgba(0, 0, 0, 0.55)',
        zIndex: 10000,
        showProgress: true,
        overlayClickAction: 'none',
        skipScroll: false,
        buttons: ['skip', 'back', 'primary'],
      }}
      locale={{ back: 'Atrás', close: 'Cerrar', last: 'Terminar', next: 'Siguiente', skip: 'Saltar tour' }}
      styles={{
        tooltip: {
          borderRadius: 12,
          fontSize: 13,
          padding: 16,
          width: 360,
        },
        tooltipTitle: {
          fontSize: 15,
          fontWeight: 700,
          marginBottom: 4,
        },
        tooltipContent: {
          fontSize: 13,
          lineHeight: 1.5,
          padding: '8px 0',
        },
        buttonNext: {
          background: 'var(--accent)',
          color: '#fff',
          fontSize: 13,
          fontWeight: 600,
          padding: '8px 14px',
          borderRadius: 8,
        },
        buttonBack: {
          color: 'var(--text-muted)',
          fontSize: 13,
          marginRight: 8,
        },
        buttonSkip: {
          color: 'var(--text-dim)',
          fontSize: 12,
        },
        buttonClose: {
          width: 10,
          height: 10,
          top: 12,
          right: 12,
        },
      }}
    />
  );
}
